import React, { useRef, useState } from 'react';

import { FacebookShareButton, TwitterShareButton, EmailShareButton, WhatsappShareButton, LinkedinShareButton } from 'react-share';
import { QRCodeCanvas } from 'qrcode.react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import EmailIcon from '@mui/icons-material/Email';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Typography from '@mui/material/Typography';
import { useDispatch, useSelector } from 'react-redux';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookRoundedIcon from '@mui/icons-material/FacebookRounded';

import slugify from 'react-slugify';
import { TwitterIcon } from '../Icons';
import { WEB_URL } from '../../api/URLs';
import { S3_BUCKET } from '../../constants';
import { ModalLayout } from './BigModalLayout';
import { MixPanelTrackedLink, Textfield } from '..';
import Tracking from '../../services/tracking.service';
import { fundRaiserPageAction } from '../../store/actions';
import { useNonInitialEffect } from '../../hooks/UseNonInitialEffect';
import { StyledShareFundraiserModal } from './styles/ShareFundraiserStyles';
import { ContentForBackToSchoolCampaign } from './EmailContentForCampaigns';

const wrapText = (ctx, text, x, y, maxWidth, lineHeight) => {
	const words = text.split(' ');
	let line = '';
	const lines = [];

	words.forEach((word, i) => {
		const testLine = line + word + ' ';
		if (ctx.measureText(testLine).width > maxWidth && i > 0) {
			lines.push(line.trim());
			line = word + ' ';
		} else {
			line = testLine;
		}
	});
	lines.push(line.trim());

	lines.forEach((l, i) => ctx.fillText(l, x, y + i * lineHeight));

	return lines.length * lineHeight;
};

/** Draws (but does not fill/stroke) a rounded rectangle path. */
const drawRoundedRect = (ctx, x, y, w, h, r) => {
	ctx.beginPath();
	ctx.moveTo(x + r, y);
	ctx.arcTo(x + w, y, x + w, y + h, r);
	ctx.arcTo(x + w, y + h, x, y + h, r);
	ctx.arcTo(x, y + h, x, y, r);
	ctx.arcTo(x, y, x + w, y, r);
	ctx.closePath();
};

// -----------------------------------------------------------------------------

export function ShareFundraiserModal({ openModal, setOpenModal }) {
	const dispatch = useDispatch();
	const [buttonText, setButtonText] = useState('Copy');
	const pageDetails = useSelector((state) => state.fundraiser_page.fundraiser_details);

	// ref to the WRAPPER around the hidden, high-res QR canvas used for the downloadable card.
	// We grab the <canvas> via querySelector instead of ref-forwarding on QRCodeCanvas itself,
	// since ref-forwarding-to-canvas is only reliable on qrcode.react v3+.
	const qrDownloadWrapperRef = useRef(null);

	// const shareUrl = `${process.env.REACT_APP_URL}/fundraiser/${pageDetails.event_id + '/' + slugify(pageDetails.page_title)}`;
	const shareUrl = `${process.env.REACT_APP_URL}${WEB_URL.FUNDRAISER_DETAILS(pageDetails.event_id, slugify(pageDetails.page_title))}`;

	// builds a copy of shareUrl tagged with UTM params for a given medium (facebook, twitter, etc.)
	const buildShareUrl = (medium) => {
		const url = new URL(shareUrl);
		url.searchParams.set('utm_source', 'p2p');
		url.searchParams.set('utm_medium', medium);
		url.searchParams.set('utm_campaign', 'fundraiser');
		url.searchParams.set('utm_content', String(pageDetails.event_id));
		return url.toString();
	};

	const qrShareUrl = buildShareUrl('qrcode');
	const facebookShareUrl = buildShareUrl('facebook');
	const twitterShareUrl = buildShareUrl('twitter');
	const whatsappShareUrl = buildShareUrl('whatsapp');
	const linkedinShareUrl = buildShareUrl('linkedin');
	const emailShareUrl = buildShareUrl('email');
	const copyShareUrl = buildShareUrl('copy');

	const shareTitle = pageDetails.page_title;
	const fbCoverImage = pageDetails.cover_image ? S3_BUCKET + pageDetails.cover_image : '';
	let emailBody = null;
	let emailSubject = null;
	if (pageDetails.campaign_name == "TCF Canada's Literacy Challenge") {
		emailBody = `Hello,

I am excited to share that I am participating in TCF Canada’s Literacy Challenge! As part of the challenge, I will be spending six hours on Sunday, April 8th in the proverbial shoes of an illiterate person by giving up my ability to read or write – no texts, no social media, no email or even Google Maps!
        
I signed up for the challenge because literacy is a human right and TCF Canada is a the forefront of providing quality education for the underprivileged; being ranked a Top 10 Impact Charity by Charity Intelligence Canada for three years running. Over the course of 30 days, the campaign aims to raise ~$630,000, which is enough to educate ~3,500 children for a year! Personally, I have committed to raising ${pageDetails.symbol_left} ${pageDetails.fundraising_goal} as part of ${pageDetails.page_title}. I hope you will consider making a donation and helping me spread the word about this Challenge!
        
Here is the link to the fundraising page:
        
${emailShareUrl}
        
With gratitude,
        
${pageDetails.first_name} ${pageDetails.last_name}`;

		emailSubject = `Please support ${shareTitle} in raising funds for the The Literacy Challenge!`;
	} else if (pageDetails.campaign_name == "TCF Canada's Back-to-School Literacy Challenge") {
		emailSubject = `Please support ${shareTitle} in raising funds for the The Literacy Challenge!`;
		emailBody = ContentForBackToSchoolCampaign(
			emailShareUrl,
			`${pageDetails.first_name} ${pageDetails.last_name}`,
			`${pageDetails.symbol_left} ${pageDetails.fundraising_goal}`,
			pageDetails.page_title,
		);
	} else {
		emailBody = `Hello,

I thought you might be interested in supporting this Fundraiser, ${emailShareUrl}.
        
Even a small donation could help ${pageDetails.first_name} ${pageDetails.last_name} reach their fundraising goal. And if you can't make a donation, it would be great if you could share the fundraiser to help spread the word.
        
Thanks for taking a look!`;

		emailSubject = `Have you seen "${shareTitle}"`;
	}

	const handleShareClick = (medium) => {
		let payload = {
			fundraiser_page_id: pageDetails.id,
			network: medium,
		};

		Tracking.trackLink(medium, 'button', 'Social media share icon on share fundraiser modal');

		dispatch(fundRaiserPageAction.addSocialCount(payload));
	};

	const copyToClipboard = (url) => {
		let textField = document.createElement('textarea');
		textField.innerText = url;
		document.body.appendChild(textField);
		textField.select();
		document.execCommand('copy');
		textField.remove();
		setButtonText('Copied!');
	};

	// ---- downloadable QR card ------------------------------------------------

	const handleDownloadQrCard = () => {
		const wrapperEl = qrDownloadWrapperRef.current;
		const qrCanvas = wrapperEl?.querySelector('canvas');

		if (!qrCanvas) {
			console.error(
				'[QR download] ABORTING: no <canvas> found inside qrDownloadWrapperRef. ' +
					'Check that QRCodeCanvas is actually rendering a <canvas> element ' +
					'(inspect the hidden wrapper in devtools — is it empty? is it an <svg> instead?).',
			);
			return;
		}

		try {
			// slightly smaller overall card than before
			const W = 560,
				H = 620;
			const canvas = document.createElement('canvas');
			canvas.width = W;
			canvas.height = H;
			const ctx = canvas.getContext('2d');

			// background — swap for a brand/campaign color if you have one on pageDetails
			ctx.fillStyle = '#005F35';
			ctx.fillRect(0, 0, W, H);

			// fundraiser name up top (in place of the logo/quote)
			ctx.fillStyle = '#FFFFFF';
			ctx.font = 'bold 34px Arial, sans-serif';
			ctx.textAlign = 'center';
			const titleTop = 60;
			const titleHeight = wrapText(ctx, pageDetails.page_title, W / 2, titleTop, W - 80, 42);

			// white rounded box behind the QR code — bigger than before, sized off
			// whatever room the (possibly multi-line) title left above it
			const boxSize = 340;
			const boxX = (W - boxSize) / 2;
			const boxY = titleTop + titleHeight + 30;
			ctx.fillStyle = '#FFFFFF';
			drawRoundedRect(ctx, boxX, boxY, boxSize, boxSize, 24);
			ctx.fill();

			// draw the actual QR code into the box — smaller padding so the QR fills more of the box
			const pad = 20;
			ctx.drawImage(qrCanvas, boxX + pad, boxY + pad, boxSize - pad * 2, boxSize - pad * 2);

			// simple caption below the box
			ctx.fillStyle = '#FFFFFF';
			ctx.font = '22px Arial, sans-serif';
			ctx.fillText('Scan to view this fundraiser', W / 2, boxY + boxSize + 45);

			// export + trigger download
			canvas.toBlob((blob) => {
				if (!blob) {
					console.error('[QR download] ABORTING: toBlob returned null (this usually means the canvas is tainted by cross-origin content).');
					return;
				}
				const url = URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				link.download = `${slugify(pageDetails.page_title)}-qr-code.png`;
				document.body.appendChild(link);
				link.click();
				link.remove();
				URL.revokeObjectURL(url);
			}, 'image/png');
		} catch (err) {
			console.error('[QR download] threw an exception:', err);
		}

		handleShareClick('qrcode_download');
	};

	// ---------------------------------------------------------------------------

	useNonInitialEffect(() => {
		if (!openModal) {
			setButtonText('Copy');
			dispatch(fundRaiserPageAction.getSocialCount(pageDetails.id));
		}
	}, [openModal]);

	return (
		<ModalLayout open={openModal} title="Share your fundraiser" setOpen={setOpenModal}>
			<StyledShareFundraiserModal>
				<Box className="QrCodeSection" aria-label="Fundraiser QR code" sx={{ position: 'relative' }}>
					<QRCodeCanvas value={qrShareUrl} size={180} includeMargin />
					<Typography variant="small">
						Scan to open this fundraiser. Click{' '}
						<Box
							component="span"
							role="button"
							tabIndex={0}
							className="QrDownloadTrigger"
							onClick={handleDownloadQrCard}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									handleDownloadQrCard();
								}
							}}
							aria-label="Download QR code"
						>
							
							here
						</Box>{' '}
						to download
					</Typography>

					<Box ref={qrDownloadWrapperRef} sx={{ position: 'absolute', left: -9999, top: -9999 }}>
						<QRCodeCanvas value={qrShareUrl} size={600} includeMargin />
					</Box>
				</Box>
				<Box className="IconsStack">
					<FacebookShareButton url={facebookShareUrl} imageUrl={fbCoverImage} onClick={() => handleShareClick('facebook')}>
						<FacebookRoundedIcon sx={{ color: '#1E4FAD' }} fontSize="large" />
						<Typography variant="small">Facebook</Typography>
					</FacebookShareButton>

					<TwitterShareButton url={twitterShareUrl} title={shareTitle} onClick={() => handleShareClick('twitter')}>
						<TwitterIcon width={35} />
						<Typography variant="small">X.com</Typography>
					</TwitterShareButton>

					<EmailShareButton
						url={''}
						title={shareTitle}
						subject={emailSubject}
						body={emailBody}
						openShareDialogOnClick={true}
						onClick={() => handleShareClick('email')}
					>
						<EmailIcon sx={{ color: '#B3B8B9' }} fontSize="large" />
						<Typography variant="small">Email</Typography>
					</EmailShareButton>

					<WhatsappShareButton url={whatsappShareUrl} title={shareTitle} onClick={() => handleShareClick('whatsapp')}>
						<WhatsAppIcon sx={{ color: '#0B8F21' }} fontSize="large" />
						<Typography variant="small">WhatsApp</Typography>
					</WhatsappShareButton>

					<LinkedinShareButton url={linkedinShareUrl} title={shareTitle} onClick={() => handleShareClick('linkedin')}>
						<LinkedInIcon sx={{ color: '#007AB5' }} fontSize="large" />
						<Typography variant="small">LinkedIn</Typography>
					</LinkedinShareButton>
				</Box>
				<Divider sx={{ mt: 4, mb: 4 }} />
				<Stack className="CopyUrlStack">
					<Box className="CopyUrlBox">
						<Typography className="CopyUrlText">{copyShareUrl}</Typography>
						<MixPanelTrackedLink
							type="button"
							size="small"
							variant="contained"
							className="CopyUrlButton"
							disabled={buttonText === 'Copied!'}
							onClick={() => copyToClipboard(copyShareUrl)}
							context={`Copied fundraiser url of ${copyShareUrl}`}
						>
							{buttonText === 'Copied!' ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
						</MixPanelTrackedLink>
					</Box>
				</Stack>
			</StyledShareFundraiserModal>
		</ModalLayout>
	);
}
